import {
  ClickAwayListener,
  Fade,
  FormControl,
  FormHelperText,
  InputAdornment,
  Popper,
  TextField,
} from "@mui/material";
import {
  ChangeEvent,
  FC,
  FocusEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { HexColorPicker } from "react-colorful";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import useEvent from "react-use-event-hook";

import { useDynamicFormsTranslate } from "@ryde/redux";
import {
  getRegisterAttributes,
  ICustomField,
  isValidHex,
  normalizeHex,
  ValidationHelpers,
} from "@ryde/shared";
import { rydeColors, ScreenDepth } from "@ryde/theme";

import { FullWidthTextFieldWrapper } from "../../parts-styled";
import { PICKER_FALLBACK_COLOR } from "./color-picker.constants";
import {
  ColorPickerPopoverContent,
  ColorPickerTemplateStyled,
  ColorSwatchButton,
} from "./color-picker.styled";

export const ColorPickerTemplate: FC<ICustomField> = ({
  id,
  name,
  size,
  formLabel,
  labelTranslated,
  formAttributes,
  formValidation,
  e2eData = "color-picker-template",
  uniqueAttrId,
}) => {
  const { control, setValue } = useFormContext();
  const generatedId = useId();
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
    translatedText: labelTranslated,
  });
  const { disabled } = getRegisterAttributes(formAttributes);

  const formValue = useWatch({ control, name: name as string }) as
    string | undefined;
  const formValueNormalized = formValue || "";

  // Local state for free typing — form gets the value only on blur with a valid hex.
  const [draftValue, setDraftValue] = useState<string>(formValueNormalized);

  useEffect(() => {
    setDraftValue(formValueNormalized);
  }, [formValueNormalized]);

  const swatchRef = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const requiredComputed = useMemo(
    () => ValidationHelpers.hasRequiredRule(formValidation),
    [formValidation],
  );

  const commitValue = useEvent((nextValue: string): void => {
    setValue(name as string, nextValue, {
      shouldDirty: true,
      shouldValidate: true,
    });
  });

  const handleSwatchClick = useEvent((): void => {
    if (disabled) {
      return;
    }

    setIsOpen(prev => !prev);
  });

  const handlePickerChange = useEvent((nextHex: string): void => {
    setDraftValue(nextHex);
    commitValue(nextHex);
  });

  const handleTextChange = useEvent(
    (event: ChangeEvent<HTMLInputElement>): void => {
      setDraftValue(event.target.value);
    },
  );

  const handleTextBlur = useEvent(
    (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      const raw = event.target.value;

      if (!raw.trim()) {
        setDraftValue("");
        commitValue("");
        return;
      }

      const normalized = normalizeHex(raw);

      if (isValidHex(normalized)) {
        setDraftValue(normalized);
        commitValue(normalized);
        return;
      }

      // Invalid input — discard and revert to the last committed form value.
      setDraftValue(formValueNormalized);
    },
  );

  if (!name) {
    return null;
  }

  const swatchColor = isValidHex(formValueNormalized)
    ? formValueNormalized
    : "";
  const pickerColor = isValidHex(formValueNormalized)
    ? formValueNormalized
    : PICKER_FALLBACK_COLOR;
  const inputId = uniqueAttrId ?? `${name}-${generatedId}`;

  return (
    <FullWidthTextFieldWrapper>
      <Controller
        name={name}
        control={control}
        render={({ fieldState: { error } }) => {
          return (
            <ColorPickerTemplateStyled>
              <FormControl
                required={requiredComputed}
                data-e2e-template="color-picker-template"
                data-e2e-field-name={name}
                error={!!error}
                fullWidth
              >
                <TextField
                  data-e2e={e2eData}
                  data-e2e-id={`${name}_${id || ""}`}
                  label={formLabelTranslated}
                  required={requiredComputed}
                  disabled={disabled}
                  error={!!error}
                  size={size}
                  fullWidth
                  value={draftValue}
                  onChange={handleTextChange}
                  onBlur={handleTextBlur}
                  placeholder={rydeColors.black}
                  id={inputId}
                  slotProps={{
                    htmlInput: {
                      dir: "ltr",
                      maxLength: 7,
                    },
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <ColorSwatchButton
                            ref={swatchRef}
                            $color={swatchColor}
                            $disabled={disabled}
                            disabled={disabled}
                            onClick={handleSwatchClick}
                            aria-label="Open color picker"
                            aria-haspopup="dialog"
                            aria-expanded={isOpen}
                          />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                {error && <FormHelperText>{error.message}</FormHelperText>}
              </FormControl>

              <Popper
                open={isOpen}
                anchorEl={swatchRef.current}
                placement="bottom-start"
                transition
                style={{ zIndex: ScreenDepth.COLOR_PICKER_POPPER }}
              >
                {({ TransitionProps }) => (
                  <Fade {...TransitionProps} timeout={150}>
                    <div>
                      <ClickAwayListener onClickAway={() => setIsOpen(false)}>
                        <ColorPickerPopoverContent role="dialog">
                          <HexColorPicker
                            color={pickerColor}
                            onChange={handlePickerChange}
                          />
                        </ColorPickerPopoverContent>
                      </ClickAwayListener>
                    </div>
                  </Fade>
                )}
              </Popper>
            </ColorPickerTemplateStyled>
          );
        }}
      />
    </FullWidthTextFieldWrapper>
  );
};
